import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  cloneDeep,
  filter,
  find,
  isEmpty,
  merge,
  pick,
  set,
  uniq,
  uniqBy,
  without,
  omitBy,
  isNumber,
} from 'lodash'
import queryString from 'query-string'

import { appFetch } from 'domains/application/appFetch'
import appSlice from 'domains/application/slice'
import { AppDispatch, AppGetState } from 'domains/application/store'
import {
  Policy,
  Customer,
  User,
  FuelCard,
  FuelCardTransaction,
  UUID7,
  Source,
  InvoiceLocation,
  CategoryCode,
  SourceType,
  Invoice,
  FuelUnit,
  CurrencyCode,
  PriceMarkup,
  FuelCardTransactionItem,
  USState,
  CanadianProvince,
  CategoryCodeDescription,
  ExternalDriver,
  FuelCardTransactionItemStatus,
  Location,
  CountryCode,
  MoneyCode,
  Receipt,
} from 'domains/organization/data-types'

export interface DataRecord {
  id: UUID7
}

export interface PaginatedFilters {
  page: {
    value: number | null
  }
  limit: {
    value: number | null
  }
}

export interface CustomerFilters extends PaginatedFilters {
  customer?: {
    value: string | null
    operation?: 'in' | 'like'
    order?: 'asc' | 'desc'
  }
}
export interface TypeFilters extends PaginatedFilters {
  type?: {
    value: CategoryCode[] | null
    operation?: 'in' | 'like'
    order?: 'asc' | 'desc'
  }
}

export interface TimeRangeFilters extends PaginatedFilters {
  start?: {
    value: string | null
    operation?: 'in'
  }
  end?: {
    value: string | null
    operation?: 'in'
  }
}

export interface SourceFilters extends PaginatedFilters {
  source?: {
    value: SourceType[] | null
    operation?: 'in'
  }
}

export interface CurrencyFilters extends PaginatedFilters {
  currency?: {
    value: CurrencyCode[] | null
    operation?: 'in'
  }
}

export interface LocationFilters extends PaginatedFilters {
  location?: {
    value: Array<USState | CanadianProvince> | null
    operation?: 'in'
  }
}

export interface DriverFilters extends PaginatedFilters {
  driver?: {
    value: Array<string> | null
    operation?: 'in'
  }
}

export interface CardFilters extends PaginatedFilters {
  card?: {
    value: Array<string> | null
    operation?: 'in'
  }
}

export interface StatusFilters extends PaginatedFilters {
  status?: {
    value: FuelCardTransactionItemStatus | null
    operation?: 'in'
  }
}

export interface InvoicedFilters extends PaginatedFilters {
  invoiced?: {
    value: 'all' | 'invoiced' | 'not_invoiced' | null
  }
}

export interface CreatedViaFilters extends PaginatedFilters {
  created_via?: {
    value: 'all' | 'manual' | 'efs' | 'manual_ui' | 'csv_import' | 'spol' | null
  }
}

export interface FuelCardFilters
  extends PaginatedFilters,
    CustomerFilters,
    SourceFilters,
    CardFilters,
    StatusFilters {}
export interface FuelCardTransactionItemFilters
  extends PaginatedFilters,
    CustomerFilters,
    TypeFilters,
    TimeRangeFilters,
    CurrencyFilters,
    SourceFilters,
    LocationFilters,
    CardFilters,
    DriverFilters,
    StatusFilters,
    InvoicedFilters,
    CreatedViaFilters {
  transaction_id?: { value: string | null }
  pricing_compare?: {
    value: string | null
  }
}

export interface InvoiceFilters extends PaginatedFilters {
  status?: {
    value: string | null
  }
  customer?: {
    value: string | null
  }
  currency?: {
    value: string | null
  }
  name?: {
    value: string | null
  }
  truck?: {
    value: string | null
  }
  updated_from?: {
    value: string | null
  }
  updated_to?: {
    value: string | null
  }
}

export interface FilterDomainState<T> {
  filters: T
  filtersOpen: boolean
  hasFilters: boolean
}

export interface Domain<T, TFilters extends PaginatedFilters = PaginatedFilters>
  extends FilterDomainState<TFilters> {
  fetching: boolean
  records: T[]
  recordsById: Record<UUID7, T>
  recordsByPage: Record<string, T[]>
  recordsByOffset: Record<number, T>
  totalCount: number
  error?: Record<string, string>
  creating: boolean
  name: keyof State
  pagePath: string
  confirmDeleteOpen: boolean
}

export interface JobGroup {
  average_runtime: number
  group_id: UUID7
  delta: string
  name: string
  task_count: number
  source_id: UUID7
  tasks: {
    id: UUID7
    state: 'scheduled' | 'retryable' | 'available' | 'executing'
    scheduled_at?: string
    args: {
      to: string
      from: string
    }
    meta: {
      id: string
      task_count: number
      group_id: string
      manual?: string | null
    }
  }[]
}

export interface State extends Record<string, unknown> {
  sourcesToColor?: Record<Source['id'], string>
  policies: Domain<Policy>
  locations: Domain<Location>
  customers: Domain<Customer> & {
    currentPage: number
    pricingHistoricalDate: null | string
  }
  users: Domain<User> & {
    create: {
      name?: string | null
      email?: string | null
      permissions?: number | null
      customerId?: string | null
      externalDrivers?: UUID7[] | null
    }
    currentPage: number
  }
  fuelCards: Domain<FuelCard, FuelCardFilters> & {
    blockAllCardsFetching: 'active' | 'inactive' | null
    blockAllCardsMessage: { text: string; severity: 'success' | 'error' | 'info' } | null
  }
  fuelCardTransactionItems: Domain<
    FuelCardTransactionItem,
    FuelCardTransactionItemFilters
  > & {
    selectedInvoiceId: UUID7 | null
    editingOverrides: boolean
    preview: FuelCardTransactionItem | null // used in overrides
    previewOverrides?: {
      adjustment_amount?: number
      adjustment_amount_reason?: string
      override_price_per_unit?: string
      override_price_per_unit_reason?: string
    }
    addToInvoice: {
      invoiceId: UUID7 | null
    }
    newItem: {
      record: {
        customer_id: UUID7 | null
        location_id: UUID7 | null
        card_id: UUID7 | null
        transaction_at: string | null
        billing_currency_code: CurrencyCode | null
        billing_country_code: CountryCode | null
        location: {
          name?: string | null
          state?: string | null
          city?: string | null
          country_code?: string | null
        }
        items: [
          {
            amount: string | null
            category: string | null
            unit: string | null
            quantity: string | null
            description?: string | null
          },
        ]
      }
      fetching: boolean
      errors: Record<string, string> | null
    }
  }
  invoices: Domain<Invoice, InvoiceFilters> & {
    create: {
      customerId: UUID7 | null
      locationId: UUID7 | null
      fuelCardId: UUID7 | null
      fuelUnit: FuelUnit | null
      currencyCode: CurrencyCode | null
      name: string | null
      dueDate: string | null
      invoiceDate: string | null
      startAt: string | null
      endAt: string | null
    }
  }
  categories: {
    fetching: boolean
    records: Array<keyof typeof CategoryCodeDescription>
  }
  categoryGroups: {
    fetching: boolean
    records: Array<string>
  }
  sources?: Domain<Source>
  externalDrivers?: Domain<ExternalDriver>
  moneyCodes?: Domain<MoneyCode>
  receipts?: Domain<Receipt>
  jobs: {
    jobGroups: JobGroup[]
    fetching: boolean
    refresh: {
      fetching: boolean
      id: string[] | null
      manualRefresh: boolean
    }
  }
}

interface Metadata {
  after?: string | null
  before?: string | null
  cursor: string | null
  limit: number
  offset: number
  total_count: number
}

const defaultDomainState = {
  fetching: false,
  records: [],
  recordsByPage: {},
  recordsById: {},
  recordsByOffset: {},
  totalCount: 0,
  creating: false,
  confirmDeleteOpen: false,
  hasFilters: false,
  filtersOpen: true,
  filters: {
    page: {
      value: 0,
    },
    limit: {
      value: 10,
    },
    status: {
      value: null,
    },
  },
}

type BaseEntity = { id: UUID7 }

interface StateDomain<EntityType extends BaseEntity> {
  request<S extends State>(state: S, action: PayloadAction<undefined>): void
  receive<S extends State>(
    state: S,
    action: PayloadAction<{
      records: EntityType[]
      merge?: boolean
      metadata?: Metadata
      // sortBy?: [string, string, string][]
    }>,
  ): void
  reject<S extends State>(
    state: S,
    action: PayloadAction<{ error: Record<string, string> }>,
  ): void
  del<S extends State>(state: S, action: PayloadAction<{ id: UUID7 }>): void
}

const createDomain = <T extends DataRecord>(domain: string): StateDomain<T> => {
  function request(state: State, _action: PayloadAction<undefined>): void {
    const s = state[domain] as Domain<T>
    s.fetching = true
  }

  function receive(
    state: State,
    action: Parameters<StateDomain<T>['receive']>[1],
  ) {
    const s = state[domain] as Domain<T>
    s.fetching = false
    s.error = undefined

    const newRecords: T[] = []

    // FIXME: this isn't perfect because order might be broken on merge - most of the time it's fine
    // it'll requery but it's annoying
    if (action.payload.merge !== false) {
      action.payload.records.forEach((record) => {
        const existingIndex = s.records.findIndex((r) => r.id === record.id)
        if (existingIndex === -1) {
          newRecords.push(record)
        } else {
          const existingRecord = s.records[existingIndex]
          s.records[existingIndex] = { ...existingRecord, ...record }
        }
      })
      s.records = [...s.records, ...newRecords]
    } else {
      s.records = action.payload.records
    }

    s.recordsById = s.records.reduce((acc: Record<UUID7, T>, record: T) => {
      acc[record.id] = record
      return acc
    }, {})

    if (
      action.payload.metadata != null &&
      action.payload.metadata?.offset != null
    ) {
      const offset = (s.filters.page.value ?? 0) * action.payload.metadata.limit
      s.recordsByOffset = s.records.reduce(
        (acc: Record<UUID7, T>, record: T, index: number) => {
          acc[offset + index] = record
          return acc
        },
        {},
      )
      const pageKey = `${s.filters.page.value ?? 0}_${action.payload.metadata.limit}`
      s.recordsByPage[pageKey] = action.payload.records
    }

    s.totalCount = action.payload.metadata?.total_count ?? 0
  }

  const reject = (
    state: State,
    action: PayloadAction<{ error: Record<string, string> }>,
  ) => {
    const s = state[domain] as Domain<T>
    s.fetching = false
    s.error = action.payload.error
  }

  const del = (state: State, action: PayloadAction<{ id: UUID7 }>) => {
    const s = state[domain] as Domain<T>
    s.records = s.records.filter((record) => record.id !== action.payload.id)
    if (action.payload.id in s.recordsById) {
      delete s.recordsById[action.payload.id]
    }
  }

  return {
    request,
    receive,
    reject,
    del,
  }
}

const initialState = {
  policies: {
    ...defaultDomainState,
    name: 'policies',
    pagePath: '/app/policies',
  },
  locations: {
    ...defaultDomainState,
    name: 'locations',
    pagePath: '/app/locations',
  },
  customers: {
    ...defaultDomainState,
    name: 'customers',
    currentPage: 0,
    pricingHistoricalDate: null,
    pagePath: '/app/customers',
    filters: {
      ...defaultDomainState.filters,
      name: { value: null as string | null },
    },
  },
  users: {
    ...defaultDomainState,
    name: 'users',
    create: {
      name: null,
      email: null,
      permissions: null,
      customerId: null,
      externalDrivers: null,
    },
    currentPage: 0,
    pagePath: '/app/users',
    filters: {
      ...defaultDomainState.filters,
      name: { value: '' },
      email: { value: '' },
      customer_id: { value: [] as UUID7[] },
      permissions: { value: 'all' as number | 'all' },
      confirmed: { value: 'all' as 'all' | 'confirmed' | 'unconfirmed' },
    },
  },
  fuelCards: {
    ...defaultDomainState,
    name: 'fuelCards',
    blockAllCardsFetching: null,
    blockAllCardsMessage: null,
    filters: {
      ...defaultDomainState.filters,
      card: { value: [] as string[] },
    },
    pagePath: '/app/fuel-cards',
  },
  invoices: {
    ...defaultDomainState,
    name: 'invoices',
    create: {
      customerId: null,
      locationId: null,
      fuelCardId: null,
      fuelUnit: null,
      currencyCode: null,
      name: null,
      dueDate: null,
      invoiceDate: null,
      startAt: null,
      endAt: null,
    },
    filters: {
      ...defaultDomainState.filters,
      status: { value: null },
      customer: { value: null },
      currency: { value: null },
      name: { value: null },
      truck: { value: null },
      updated_from: { value: null },
      updated_to: { value: null },
    },
    pagePath: '/app/invoices',
  },
  fuelCardTransactionItems: {
    ...defaultDomainState,
    name: 'fuelCardTransactionItems',
    filters: {
      ...defaultDomainState.filters,
      transaction_id: { value: null },
      source: {
        value: null,
      },
      customer: {
        value: null,
      },
      type: {
        value: null,
      },
      currency: {
        value: null,
      },
      location: {
        value: null,
      },
      driver: {
        value: null,
      },
      card: {
        value: null,
      },
      start: {
        value: null,
      },
      end: {
        value: null,
      },
      created_via: {
        value: null,
      },
    },
    pagePath: '/app/fuel-card-transaction-items',
    editingOverrides: false,
    selectedInvoiceId: null,
    preview: null,
    addToInvoice: {
      invoiceId: null,
    },
    newItem: {
      record: {
        customer_id: null,
        location_id: null,
        card_id: null,
        transaction_at: null,
        billing_currency_code: null,
        billing_country_code: null,
        items: [
          {
            amount: null,
            category: null,
            unit: null,
            quantity: null,
          },
        ],
        location: {
          name: null,
          country_code: null,
          state: null,
          city: null,
        },
      },
      fetching: false,
      errors: null,
    },
  },
  externalDrivers: {
    ...defaultDomainState,
    name: 'externalDrivers',
    pagePath: '/app/external-drivers',
  },
  moneyCodes: {
    ...defaultDomainState,
    name: 'moneyCodes',
    pagePath: '/app/money-codes',
    filters: {
      ...defaultDomainState.filters,
      customer_id: { value: [] as UUID7[] },
      created_from: { value: null as string | null },
      created_to: { value: null as string | null },
      activated_from: { value: null as string | null },
      activated_to: { value: null as string | null },
      first_used_from: { value: null as string | null },
      first_used_to: { value: null as string | null },
      used: { value: 'all' as 'all' | 'used' | 'unused' },
      billed: { value: 'all' as 'all' | 'billed' | 'not_billed' },
    },
    addToInvoice: {
      invoiceId: null as UUID7 | null,
    },
  },
  receipts: {
    ...defaultDomainState,
    name: 'receipts',
    pagePath: '/app/receipts',
  },
  categories: {
    fetching: false,
    records: [
      'ULSD',
      'DEFD',
      'RFR',
      'ULSR',
      'SCLE',
      'BDSL',
      'FUEL',
      'CFEE',
      'ONFEE',
      'CUSTOM',
    ],
  },
  categoryGroups: {
    fetching: false,
    records: [],
  },
  jobs: {
    fetching: false,
    jobGroups: [],
    refresh: {
      fetching: false,
      id: null,
      manualRefresh: false,
    },
  },
} satisfies State as State

const fuelCardsDomain = createDomain<FuelCard>('fuelCards')
const fuelCardTransactionItemsDomain = createDomain<FuelCardTransaction>(
  'fuelCardTransactionItems',
)
const customersDomain = createDomain<Customer>('customers')
const usersDomain = createDomain<User>('users')
const policiesDomain = createDomain<Policy>('policies')
const invoicesDomain = createDomain<Invoice>('invoices')
const sourcesDomain = createDomain<Source>('sources')
const externalDriversDomain = createDomain<ExternalDriver>('externalDrivers')
const moneyCodesDomain = createDomain<MoneyCode>('moneyCodes')
const receiptsDomain = createDomain<Receipt>('receipts')

const slice = createSlice({
  name: 'organization',
  initialState: initialState,
  reducers: (_create) => {
    return {
      'fuelCards/receive': fuelCardsDomain.receive,
      'fuelCards/request': fuelCardsDomain.request,
      'fuelCards/reject': fuelCardsDomain.reject,
      'fuelCards/setAllCards/request': (
        state: State,
        action: { payload: { status: 'active' | 'inactive' } },
      ) => {
        state.fuelCards.blockAllCardsFetching = action.payload.status
        state.fuelCards.blockAllCardsMessage = null
      },
      'fuelCards/setAllCards/message': (
        state: State,
        action: { payload: { text: string; severity: 'success' | 'error' | 'info' } },
      ) => {
        state.fuelCards.blockAllCardsMessage = action.payload
      },
      'fuelCards/setAllCards/receive': (
        state: State,
        action: { payload: { customerId: UUID7; cards?: FuelCard[] } },
      ) => {
        state.fuelCards.blockAllCardsFetching = null
        state.customers.recordsById[action.payload.customerId] = {
          ...state.customers.recordsById[action.payload.customerId],
          fuel_cards:
            action.payload.cards ??
            state.customers.recordsById[action.payload.customerId].fuel_cards,
        }
        const customer = state.customers.records.find(
          (record) => record.id === action.payload.customerId,
        )
        if (customer) {
          customer.fuel_cards = action.payload.cards ?? customer.fuel_cards
        }
      },
      'fuelCardTransactionItems/receive':
        fuelCardTransactionItemsDomain.receive,
      'fuelCardTransactionItems/request':
        fuelCardTransactionItemsDomain.request,
      'fuelCardTransactionItems/reject': fuelCardTransactionItemsDomain.reject,
      'fuelCardTransactionItems/setFilters': (
        state: State,
        action: {
          payload: {
            filtersOpen?: boolean
            filters?: Partial<FuelCardTransactionItemFilters>
            reset?: boolean
          }
        },
      ) => {
        if ('filtersOpen' in action.payload) {
          state.fuelCardTransactionItems.filtersOpen =
            action.payload.filtersOpen === true
        }
        if (action.payload.reset === true) {
          state.fuelCardTransactionItems.filters = {
            ...slice.getInitialState().fuelCardTransactionItems.filters,
          }
          state.fuelCardTransactionItems.hasFilters = false
          return
        }
        const originalFilters = cloneDeep(
          state.fuelCardTransactionItems.filters ?? {},
        )
        state.fuelCardTransactionItems.filters = merge(
          {},
          state.fuelCardTransactionItems.filters,
          cloneDeep(action.payload.filters),
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['customer', 'value'],
          uniq(
            action.payload.filters?.customer?.value ??
              originalFilters?.customer?.value,
          ),
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['type', 'value'],
          uniq(
            action.payload.filters?.type?.value ?? originalFilters?.type?.value,
          ),
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['source', 'value'],
          uniq(
            action.payload.filters?.source?.value ??
              originalFilters?.source?.value,
          ),
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['currency', 'value'],
          uniq(
            action.payload.filters?.currency?.value ??
              originalFilters?.currency?.value,
          ),
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['location', 'value'],
          uniq(
            action.payload.filters?.location?.value ??
              originalFilters?.location?.value,
          ),
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['start', 'value'],
          action.payload.filters?.start?.value ?? originalFilters?.start?.value,
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['end', 'value'],
          action.payload.filters?.end?.value ?? originalFilters?.end?.value,
        )

        set(
          state.fuelCardTransactionItems.filters,
          ['status', 'value'],
          action.payload.filters?.status?.value ??
            originalFilters?.status?.value,
        )

        const vals = Object.values(state.fuelCardTransactionItems.filters)
        const allEmpty = vals.every(
          (value) => value == null || isEmpty(value.value),
        )
        state.fuelCardTransactionItems.hasFilters = !allEmpty
      },
      'fuelCardTransactionItems/setSelectedInvoice': (
        state: State,
        { payload: { id } }: { payload: { id: UUID7 } },
      ) => {
        state.fuelCardTransactionItems.selectedInvoiceId = id
      },
      'fuelCardTransactionItems/setEditingOverrides': (
        state: State,
        { payload: { show } }: { payload: { show: boolean } },
      ) => {
        state.fuelCardTransactionItems.editingOverrides = show
      },
      'fuelCardTransactionItems/setOverrides': (
        state: State,
        {
          payload: payload,
        }: {
          payload: {
            overrides: {
              adjustment_amount?: number | null
              adjustment_amount_reason?: string | null
              override_price_per_unit?: string | null
              override_price_per_unit_reason?: string | null
            }
          }
        },
      ) => {
        state.fuelCardTransactionItems.previewOverrides = omitBy(
          payload.overrides,
          (val) => !isNumber(val) && isEmpty(val),
        )
      },
      'fuelCardTransactionItems/updatePreview': (
        state: State,
        {
          payload: { data },
        }: { payload: { data: FuelCardTransactionItem | null } },
      ) => {
        state.fuelCardTransactionItems.preview = data
        if (data == null) {
          state.fuelCardTransactionItems.previewOverrides = undefined
        }
        state.fuelCardTransactionItems.fetching = false
      },
      'fuelCardTransactionItems/selectAddToInvoice': (
        state: State,
        { payload }: { payload: { invoiceId?: UUID7 | null } },
      ) => {
        state.fuelCardTransactionItems.addToInvoice.invoiceId =
          payload?.invoiceId ?? null
      },
      'fuelCardTransactionItems/newItem/update': (
        state: State,
        {
          payload,
        }: {
          payload: {
            customer_id?: UUID7 | null
            location_id?: UUID7 | null
            card_id?: UUID7 | null
            transaction_at?: string | null
            billing_currency_code?: CurrencyCode
            billing_country_code?: CountryCode
            items?: [
              {
                category?: UUID7 | null
                amount?: string | null
                quantity?: string | null
                unit?: string | null
                description?: string | null
              },
            ]
            location?: Partial<
              Pick<Location, 'country_code' | 'city' | 'state' | 'name'>
            >
          }
        },
      ) => {
        state.fuelCardTransactionItems.newItem.record = {
          ...state.fuelCardTransactionItems.newItem.record,
          ...payload,
          items: [
            {
              ...(state.fuelCardTransactionItems.newItem.record.items[0] ?? {}),
              ...(payload.items?.[0] ?? {}),
            },
          ],
          location: {
            ...state.fuelCardTransactionItems.newItem.record.location,
            ...(payload.location ?? {}),
          },
        }
      },
      'fuelCardTransactionItems/newItem/request': (state: State) => {
        state.fuelCardTransactionItems.newItem = {
          ...state.fuelCardTransactionItems.newItem,
          fetching: true,
          errors: null,
        }
      },
      'fuelCardTransactionItems/newItem/receive': (
        state: State,
        {
          payload,
        }: {
          payload: {
            errors?: Record<string, string>
            data?: FuelCardTransactionItem
          }
        },
      ) => {
        state.fuelCardTransactionItems.newItem = {
          ...state.fuelCardTransactionItems.newItem,
          fetching: false,
          errors: payload?.errors || null,
        }
      },
      'invoices/addTransactions/request': (state: State) => {
        state.invoices.fetching = true
      },
      'invoices/addTransactions/receive': (state: State) => {
        state.invoices.fetching = false
      },
      'customers/receive': customersDomain.receive,
      'customers/request': customersDomain.request,
      'customers/reject': customersDomain.reject,
      'customers/confirmDeleteToggle': _create.reducer(
        (state: State, _action) => {
          state.customers.confirmDeleteOpen = !state.customers.confirmDeleteOpen
        },
      ),
      'customers/delete': _create.reducer(
        (state: State, { payload: { id } }: { payload: { id: UUID7 } }) => {
          if (state.customers.recordsById[id] != null) {
            delete state.customers.recordsById[id]
          }
          const found = find(
            state.customers.records,
            (customer) => customer.id === id,
          )
          if (found) {
            state.customers.records = without(state.customers.records, found)
          }
        },
      ),
      'customers/setPricingHistoricalDate': _create.reducer(
        (state: State, action: { payload: { date: string | null } }) => {
          state.customers.pricingHistoricalDate = action.payload.date
        },
      ),
      'users/receive': usersDomain.receive,
      'users/request': usersDomain.request,
      'users/reject': usersDomain.reject,
      'users/delete': usersDomain.del,
      'users/updateCreate': (
        state: State,
        action: {
          payload: {
            name?: string
            email?: string
            permissions?: number
            customerId?: string
            externalDrivers?: UUID7[]
            reset?: boolean
          }
        },
      ) => {
        const updates = pick(action.payload, [
          'name',
          'email',
          'permissions',
          'customerId',
          'externalDrivers',
        ])
        if (action.payload.reset === true) {
          state.users.create = { ...slice.getInitialState().users.create }
        }
        if (action.payload.customerId != null) {
          state.users.create.externalDrivers = []
        }
        state.users.create = { ...state.users.create, ...updates }
      },
      'policies/receive': policiesDomain.receive,
      'policies/request': policiesDomain.request,
      'policies/reject': policiesDomain.reject,
      'invoices/receive': invoicesDomain.receive,
      'invoices/request': invoicesDomain.request,
      'invoices/reject': invoicesDomain.reject,
      'invoices/updateCreate': (
        state: State,
        action: {
          payload: {
            name?: string
            customerId?: string
            locationId?: string
            fuelCardId?: string | null
            fuelUnit?: FuelUnit
            currencyCode?: CurrencyCode
            dueDate?: string | null
            invoiceDate?: string | null
            startAt?: string | null
            endAt?: string | null
            reset?: boolean
          }
        },
      ) => {
        const updates = pick(action.payload, [
          'name',
          'customerId',
          'locationId',
          'fuelCardId',
          'fuelUnit',
          'currencyCode',
          'dueDate',
          'invoiceDate',
          'startAt',
          'endAt',
        ])
        if (updates.customerId != null && updates.locationId === undefined) {
          const customer =
            state.customers.recordsById[updates.customerId as string]
          const location = customer?.invoice_locations?.[0]
          updates.locationId = location?.id ?? ''
        }
        if (action.payload.reset === true) {
          state.invoices.create = { ...slice.getInitialState().invoices.create }
        }
        state.invoices.create = { ...state.invoices.create, ...updates }
      },
      'invoices/delete': _create.reducer(
        (state: State, { payload: { id } }: { payload: { id: UUID7 } }) => {
          if (state.invoices.recordsById[id] != null) {
            delete state.invoices.recordsById[id]
          }
          const found = find(
            state.invoices.records,
            (customer) => customer.id === id,
          )
          if (found) {
            state.invoices.records = without(state.invoices.records, found)
          }
        },
      ),
      'invoices/confirmDeleteToggle': _create.reducer(
        (state: State, _action) => {
          state.invoices.confirmDeleteOpen = !state.invoices.confirmDeleteOpen
        },
      ),
      'sources/receive': sourcesDomain.receive,
      'sources/request': sourcesDomain.request,
      'sources/reject': sourcesDomain.reject,
      'categories/request': (state: State) => {
        state.categories.fetching = true
      },
      'categories/receive': (
        state: State,
        action: {
          payload: { categories: (keyof typeof CategoryCodeDescription)[] }
        },
      ) => {
        state.categories.fetching = false
        state.categories.records = action.payload.categories
      },
      'categoryGroup/request': (state: State) => {
        state.categoryGroups.fetching = true
      },
      'categoryGroup/receive': (
        state: State,
        action: {
          payload: { categoryGroups: (keyof typeof CategoryCodeDescription)[] }
        },
      ) => {
        state.categoryGroups.fetching = false
        state.categoryGroups.records = action.payload.categoryGroups
      },
      'jobs/refresh/request': (state: State) => {
        state.jobs.refresh.fetching = true
      },
      'jobs/refresh/receive': (
        state: State,
        action: {
          payload: {
            id?: string[]
            errors?: Record<string, string>
          }
        },
      ) => {
        state.jobs.refresh.id = action.payload.id ?? null
        state.jobs.refresh.fetching = false
        state.jobs.refresh.manualRefresh = state.jobs.jobGroups.some((jg) =>
          jg.tasks?.some((task) => task.meta.manual != null),
        )
      },
      'jobs/request': (state: State) => {
        state.jobs.fetching = true
      },
      'jobs/receive': (
        state: State,
        action: {
          payload: {
            jobGroups: (JobGroup & { average_runtime: string })[]
          }
        },
      ) => {
        state.jobs.fetching = false
        state.jobs.jobGroups = action.payload.jobGroups.map((jobGroup) => {
          return {
            ...jobGroup,
            average_runtime: parseFloat(jobGroup.average_runtime),
          }
        })
        state.jobs.refresh.manualRefresh = state.jobs.jobGroups.some((jg) =>
          jg.tasks?.some((task) => task.meta.manual != null),
        )
      },
      'jobs/update': (
        state: State,
        action: {
          payload: {
            args: JobGroup['tasks'][number]['args']
            meta: JobGroup['tasks'][number]['meta']
          }
        },
      ) => {
        const jobIndexes = state.jobs.jobGroups.map((jg) => {
          return jg.tasks.findIndex((j) => {
            return (
              j.meta.group_id == action.payload.meta.group_id &&
              j.meta.id == action.payload.meta.id
            )
          })
        })
        const jobGroupIndex = jobIndexes.findIndex((jobIndex) => {
          return jobIndex !== -1
        })

        if (jobGroupIndex !== -1) {
          const jobIndex = jobIndexes[jobGroupIndex]
          state.jobs.jobGroups[jobGroupIndex].tasks.splice(jobIndex, 1)
        }
        state.jobs.jobGroups = state.jobs.jobGroups.slice()
      },
      'externalDrivers/request': externalDriversDomain.request,
      'externalDrivers/reject': externalDriversDomain.reject,
      'externalDrivers/receive': externalDriversDomain.receive,
      'moneyCodes/request': moneyCodesDomain.request,
      'moneyCodes/reject': moneyCodesDomain.reject,
      'moneyCodes/receive': moneyCodesDomain.receive,
      'receipts/request': receiptsDomain.request,
      'receipts/reject': receiptsDomain.reject,
      'receipts/receive': receiptsDomain.receive,
      'moneyCodes/selectAddToInvoice': (
        state: State,
        { payload }: { payload: { invoiceId?: UUID7 | null } },
      ) => {
        state.moneyCodes.addToInvoice.invoiceId =
          payload?.invoiceId ?? null
      },
    }
  },
  extraReducers: (builder) => {
    builder.addCase(appSlice.actions.setPath, (state, action) => {
      let targetKey:
        | keyof Pick<
            State,
            | 'fuelCards'
            | 'users'
            | 'customers'
            | 'policies'
            | 'fuelCardTransactionItems'
            | 'moneyCodes'
            | 'invoices'
          >
        | null = null
      if (action.payload.path.startsWith('/app/fuel-cards')) {
        targetKey = 'fuelCards'
      } else if (action.payload.path.startsWith('/app/users')) {
        targetKey = 'users'
      } else if (action.payload.path.startsWith('/app/customers')) {
        targetKey = 'customers'
      } else if (action.payload.path.startsWith('/app/policies')) {
        targetKey = 'policies'
      } else if (
        action.payload.path.startsWith('/app/fuel-card-transaction-items')
      ) {
        targetKey = 'fuelCardTransactionItems'
      } else if (action.payload.path.startsWith('/app/money-codes')) {
        targetKey = 'moneyCodes'
      } else if (action.payload.path.startsWith('/app/invoices')) {
        targetKey = 'invoices'
      } else if (action.payload.path.startsWith('/app/receipts')) {
        targetKey = 'receipts'
      }

      if (targetKey == null) {
        return
      }

      const target = state[targetKey]

      if (target != null) {
        let keys: Array<keyof unknown | string>
        keys = [
          ...Object.keys(target.filters),
          ...Object.keys(action.payload.urlArgs as Partial<unknown>),
        ]
        keys = uniq(keys)

        for (let index in keys) {
          let filter = keys[index] as keyof State[typeof targetKey]['filters']
          let value = action.payload.urlArgs?.[filter] ?? null
          let newValue

          if (['page', 'limit'].includes(filter) && typeof value === 'string') {
            newValue = parseInt(value)
          } else {
            newValue = value
          }

          if (newValue == null) {
            if (filter in initialState[targetKey].filters) {
              newValue = initialState[targetKey].filters[filter]?.value
            }
          }
          set(target, ['filters', filter, 'value'], newValue)
        }

        if (
          'currentPage' in target &&
          'page' in (action.payload?.urlArgs ?? {})
        ) {
          target.currentPage = parseInt(
            (action.payload.urlArgs?.page as string) ?? '0',
          )
        }
      }
    })
    return builder
  },
})

export const fetchPolicies =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['policies/request']())
      const url = fetchUrlWithFilters(getState().organization, 'policies')
      const { data, metadata } = await (await fetch(url)).json()
      dispatch(
        slice.actions['policies/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['policies/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchCustomers =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['customers/request']())
      const url = fetchUrlWithFilters(getState().organization, 'customers')
      const { data, metadata } = await (await fetch(url)).json()
      dispatch(
        slice.actions['customers/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export interface DuplicateGroup {
  normalized_name: string
  customers: {
    id: string
    name: string
    inserted_at: string
    fuel_card_count: number
    transaction_count: number
    invoice_count: number
  }[]
}

export const fetchDuplicateCustomers =
  () => async (_dispatch: AppDispatch): Promise<DuplicateGroup[]> => {
    try {
      const { data } = await (await fetch('/api/customers/duplicates')).json()
      return data as DuplicateGroup[]
    } catch (err) {
      console.error('Failed to fetch duplicates:', err)
      return []
    }
  }

export const mergeCustomers =
  (primaryId: string, duplicateId: string) =>
  async (dispatch: AppDispatch): Promise<boolean> => {
    try {
      const response = await fetch('/api/customers/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ primary_id: primaryId, duplicate_id: duplicateId }),
      })
      if (!response.ok) {
        const result = await response.json()
        throw new Error(result.errors?.detail || 'Merge failed')
      }
      return true
    } catch (err) {
      dispatch(
        appSlice.actions.putSnack({
          message: `Failed to merge: ${err instanceof Error ? err.message : 'Unknown error'}`,
          color: 'error',
        }),
      )
      return false
    }
  }

export const fetchCustomer =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['customers/request']())
      const { data } = await (await fetch(`/api/customers/${id}`)).json()
      dispatch(slice.actions['customers/receive']({ records: [data] }))
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const createCustomer =
  (customerInfo: { name: string }) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['customers/request']())
      const { data } = await (
        await fetch(`/api/customers`, {
          method: 'POST',
          body: JSON.stringify({ customer: customerInfo }),
          headers: {
            'Content-Type': 'application/json',
          },
        })
      ).json()
      dispatch(slice.actions['customers/receive']({ records: [data] }))
      return data
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const createInvoiceLocation =
  (location: Partial<InvoiceLocation>) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['customers/request']())

      const request = await fetch(
        `/api/customers/${location.customer_id}/invoice_locations`,
        {
          method: 'POST',
          body: JSON.stringify({ invoice_location: location }),
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      const { data, errors } = await request.json()

      if (errors != null) {
        throw errors
      }

      const customerId = data.customer_id
      const customer = cloneDeep(
        getState().organization.customers.recordsById[customerId],
      )
      const invoiceLocations = customer.invoice_locations || []
      invoiceLocations.push(data)
      customer.invoice_locations = uniqBy(invoiceLocations, 'id')

      dispatch(
        slice.actions['customers/receive']({
          records: [customer],
          merge: true,
        }),
      )
      return data
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const deleteInvoiceLocation =
  (location: InvoiceLocation) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['customers/request']())

      await fetch(
        `/api/customers/${location.customer_id}/invoice_locations/${location.id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        },
      )
      const customer = cloneDeep(
        getState().organization.customers.recordsById[location.customer_id],
      )
      const invoiceLocations = filter(
        customer.invoice_locations,
        (loc) => loc.id !== location.id,
      )
      customer.invoice_locations = invoiceLocations

      dispatch(
        slice.actions['customers/receive']({
          records: [customer],
          // merge: true,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const deleteCustomer = (id: UUID7) => async (dispatch: AppDispatch) => {
  try {
    dispatch(slice.actions['customers/request']())
    await fetch(`/api/customers/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
    dispatch(slice.actions['customers/delete']({ id }))
  } catch (err) {
    dispatch(
      slice.actions['customers/reject']({
        error:
          err instanceof Error
            ? { unknown: String(err) }
            : (err as Record<string, string>),
      }),
    )
  }
}

export const updateCustomer =
  (id: Customer['id'], attrs: Partial<Customer>) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['customers/request']())
      const body = JSON.stringify({ customer: attrs })
      const response = await fetch(`/api/customers/${id}`, {
        body: body,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const { data, errors } = await response.json()
      if (errors != null) {
        throw errors
      }
      dispatch(slice.actions['customers/receive']({ records: [data] }))
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const updateCustomerPricing =
  (id: Customer['id'], attrs: Partial<PriceMarkup>) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['customers/request']())
      const response = await appFetch(`/api/customers/${id}/price_markups`, {
        body: { price_markup: attrs },
        method: 'POST',
      })
      const { errors } = await response.json()
      if (errors != null) {
        throw errors
      }
      // dispatch(slice.actions['customers/receive']({ records: [data] }))
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
    await dispatch(fetchCustomer(id))
  }

export const deletePriceMarkup =
  (customerId: Customer['id'], id: PriceMarkup['id']) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['customers/request']())
      await appFetch(`/api/customers/${customerId}/price_markups/${id}`, {
        method: 'DELETE',
      })
    } catch (err) {
      dispatch(
        slice.actions['customers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchUsers =
  (options?: { limit?: number }) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['users/request']())
      const url = fetchUrlWithFilters(
        getState().organization,
        'users',
        undefined,
        options,
      )
      const { data, metadata } = await (await fetch(url)).json()
      dispatch(
        slice.actions['users/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      console.error(err)
      dispatch(
        slice.actions['users/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchUser =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['users/request']())
      const { data } = await (await fetch(`/api/users/${id}`)).json()
      const receive = slice.actions['users/receive']
      dispatch(receive({ records: [data], merge: true }))
    } catch (err) {
      dispatch(
        slice.actions['users/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const updateUser =
  (id: User['id'], attrs: Partial<User>) => async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['users/request']())
      const body = JSON.stringify({ user: attrs })
      const response = await fetch(`/api/users/${id}`, {
        body: body,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const { data, errors } = await response.json()
      if (errors != null) {
        throw errors
      }
      dispatch(slice.actions['users/receive']({ records: [data], merge: true }))
    } catch (err) {
      console.error(err)
      dispatch(
        slice.actions['users/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const deleteUser =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['users/request']())
      const response = await fetch(`/api/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      dispatch(slice.actions['users/delete']({ id }))
      if (response.status > 300) {
        throw new Error(response.statusText)
      }
    } catch (err) {
      dispatch(
        slice.actions['users/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const createUser =
  // FIXME: fix attrs types


    (attrs: Record<string, unknown>) =>
    async (dispatch: AppDispatch): Promise<User> => {
      try {
        dispatch(slice.actions['users/request']())
        const body = JSON.stringify({ user: attrs })
        const response = await fetch(`/api/users`, {
          body: body,
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        })
        const { data, errors } = await response.json()
        if (errors != null) {
          throw errors
        }
        dispatch(slice.actions['users/receive']({ records: [data] }))
        return data
      } catch (err) {
        console.error(err)
        dispatch(
          slice.actions['users/reject']({
            error:
              err instanceof Error
                ? { unknown: String(err) }
                : (err as Record<string, string>),
          }),
        )
        throw err
      }
    }

export const fetchFuelCards =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCards/request']())
      const url = fetchUrlWithFilters(getState().organization, 'fuelCards')
      const { data, metadata } = await (await fetch(url)).json()
      dispatch(
        slice.actions['fuelCards/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      console.error(err)
      dispatch(
        slice.actions['fuelCards/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const updateFuelCard =
  (id: UUID7, attrs: Partial<FuelCard>) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCards/request']())
      const body = JSON.stringify({ fuel_card: attrs })
      const response = await fetch(`/api/fuel_cards/${id}`, {
        body: body,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const { data, errors } = await response.json()
      if (errors != null) {
        throw errors
      }
      dispatch(
        slice.actions['fuelCards/receive']({ records: [data], merge: true }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCards/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

/** Petro-Pass (COR-74): an admin gives a card its customer. */
export const assignFuelCardCustomer =
  (id: UUID7, customerId: UUID7) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCards/request']())
      const response = await fetch(`/api/fuel_cards/${id}/assign_customer`, {
        body: JSON.stringify({ customer_id: customerId }),
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const { data, error, errors } = await response.json()
      if (!response.ok || data == null) {
        throw error != null ? { detail: error } : (errors ?? { detail: 'Could not assign the customer.' })
      }
      dispatch(
        slice.actions['fuelCards/receive']({ records: [data], merge: true }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCards/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchFuelCard =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCards/request']())
      const { data } = await (await fetch(`/api/fuel_cards/${id}`)).json()
      dispatch(
        slice.actions['fuelCards/receive']({ records: [data], merge: true }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCards/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchFuelCardTransactionItem =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const { data } = await (
        await fetch(`/api/fuel_card_transaction_items/${id}`)
      ).json()
      dispatch(
        slice.actions['fuelCardTransactionItems/receive']({
          records: [data],
          merge: true,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

// Stubs: FuelCardTransaction (without Item) state/API not implemented
// These functions are referenced but the fuelCardTransactions state slice was never implemented

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const fetchFuelCardTransaction = (_id: UUID7) => () => {
  // TODO: Implement fuelCardTransactions state and API endpoint
  console.warn('fetchFuelCardTransaction is not implemented')
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const addTransactionToInvoice = (_transactionId: UUID7, _invoiceId: UUID7) => () => {
  // TODO: Implement adding transaction to invoice
  console.warn('addTransactionToInvoice is not implemented')
}

export type TruckMismatchInfo = {
  matching: number
  mismatched: number
  invoice_truck: string
  mismatched_trucks: string[]
  message: string
}

export type AddTransactionsResult =
  | { success: true; warning?: string }
  // Nothing was added: wrong-truck transactions in the selection require an
  // explicit choice (retry with truckMismatch: 'skip' | 'add_all').
  | { success: false; needsConfirmation: true; info: TruckMismatchInfo }
  | { success: false; needsConfirmation?: undefined; errorType: string; message: string }

export const addMultipleTransactionsToInvoice =
  (invoiceId: UUID7, opts?: { truckMismatch?: 'skip' | 'add_all'; allowBackdated?: boolean }) =>
  async (
    dispatch: AppDispatch,
    getState: AppGetState,
  ): Promise<AddTransactionsResult> => {
    try {
      dispatch(slice.actions['invoices/addTransactions/request']())
      const filters = getState().organization.fuelCardTransactionItems.filters
      const filterValues: Record<string, string | string[] | number | undefined | null> = {}

      for (const filterName in filters) {
        const filter = filters[filterName as keyof FuelCardTransactionItemFilters]
        if (filter?.value != null) {
          filterValues[filterName] = filter.value
        }
      }

      if (opts?.truckMismatch) {
        filterValues['truck_mismatch'] = opts.truckMismatch
      }

      if (opts?.allowBackdated) filterValues['allow_backdated'] = 'true'

      const qs = queryString.stringify(filterValues, { arrayFormat: 'bracket' })
      const url = `/api/invoices/${invoiceId}/add-transactions${qs ? `?${qs}` : ''}`

      const response = await fetch(url)
      const data = await response.json()

      dispatch(slice.actions['invoices/addTransactions/receive']())

      if (data.status === 'ok') {
        // Refresh the transaction items to show updated invoice_id
        dispatch(fetchFuelCardTransactionItems({}))
        // Partial success: wrong-truck transactions were skipped (truck_mismatch)
        return { success: true, warning: data.warning?.message }
      } else if (data.status === 'truck_mismatch_confirmation_required') {
        return { success: false, needsConfirmation: true, info: data.info }
      } else {
        return {
          success: false,
          errorType: data.error_type || 'unknown',
          message: data.message || 'Failed to add transactions to invoice',
        }
      }
    } catch (err) {
      dispatch(slice.actions['invoices/addTransactions/receive']())
      return {
        success: false,
        errorType: 'network_error',
        message: err instanceof Error ? err.message : 'Network error occurred',
      }
    }
  }

export const removeTransactionFromInvoice =
  (invoiceId: UUID7, itemId: UUID7) =>
  async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/transaction-items/${itemId}`, {
        method: 'DELETE',
      })
      const data = await response.json()

      if (data.status === 'ok' || response.ok) {
        // Refresh the invoice to show updated items
        dispatch(fetchInvoice(invoiceId))
        return { success: true }
      } else {
        throw new Error(data.error || 'Failed to remove transaction from invoice')
      }
    } catch (err) {
      console.error('Failed to remove transaction from invoice:', err)
      throw err
    }
  }

export const addMultipleMoneyCodesToInvoice =
  (invoiceId: UUID7) =>
  async (dispatch: AppDispatch, getState: AppGetState): Promise<{ status: 'ok' | 'error'; message?: string; error_type?: string }> => {
    try {
      dispatch(slice.actions['invoices/addTransactions/request']())
      const filters = getState().organization.moneyCodes.filters
      const filterValues: Record<string, string | string[] | number | undefined | null> = {}

      for (const filterName in filters) {
        const filter = filters[filterName as keyof typeof filters]
        if (filter?.value != null) {
          filterValues[filterName] = filter.value
        }
      }

      const qs = queryString.stringify(filterValues, { arrayFormat: 'bracket' })
      const url = `/api/invoices/${invoiceId}/add-money-codes${qs ? `?${qs}` : ''}`

      const response = await fetch(url)
      const data = await response.json()

      dispatch(slice.actions['invoices/addTransactions/receive']())

      if (data.status === 'ok') {
        // Refresh the money codes to show updated invoice_id
        dispatch(fetchMoneyCodes())
        return { status: 'ok' }
      } else {
        return { status: 'error', message: data.message, error_type: data.error_type }
      }
    } catch (err) {
      dispatch(slice.actions['invoices/addTransactions/receive']())
      return { status: 'error', message: 'Failed to add money codes to invoice' }
    }
  }

export const removeMoneyCodeFromInvoice =
  (invoiceId: UUID7, moneyCodeId: UUID7) =>
  async (dispatch: AppDispatch) => {
    try {
      const response = await fetch(`/api/invoices/${invoiceId}/money-codes/${moneyCodeId}`, {
        method: 'DELETE',
      })
      const data = await response.json()

      if (data.status === 'ok' || response.ok) {
        // Refresh the invoice to show updated money codes
        dispatch(fetchInvoice(invoiceId))
        return { success: true }
      } else {
        throw new Error(data.error || 'Failed to remove money code from invoice')
      }
    } catch (err) {
      console.error('Failed to remove money code from invoice:', err)
      throw err
    }
  }

export const editFuelCardTransactionItem =
  (id: UUID7, status: FuelCardTransactionItemStatus) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const { data } = await (
        await fetch(`/api/fuel_card_transaction_items/${id}`, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ status }),
        })
      ).json()
      dispatch(
        slice.actions['fuelCardTransactionItems/receive']({
          records: [data],
          merge: true,
          // sortBy: [['transaction_at', 'datetime', 'DESC']],
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const reassignTransactionItemCustomer =
  (id: UUID7, customerId: UUID7) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const { data } = await (
        await fetch(`/api/fuel_card_transaction_items/${id}`, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ customer_id: customerId }),
        })
      ).json()
      dispatch(
        slice.actions['fuelCardTransactionItems/receive']({
          records: [data],
          merge: true,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

// openspec 071 (COR-14): change a transaction item's category and let the
// server re-price it. The backend refuses the edit with a 422 when the item is
// already on an invoice, so surface that error rather than assuming success.
export const updateTransactionItemCategory =
  (id: UUID7, category: string) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const response = await fetch(
        `/api/fuel_card_transaction_items/${id}/category`,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ category }),
        },
      )
      const body = await response.json()

      if (!response.ok) {
        dispatch(
          slice.actions['fuelCardTransactionItems/reject']({
            error: body?.errors ?? { unknown: 'Could not update category' },
          }),
        )
        return
      }

      dispatch(
        slice.actions['fuelCardTransactionItems/receive']({
          records: [body.data],
          merge: true,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const getFetchFuelCardTransactionUrl = (
  state: State,
  fetchIds?: UUID7[],
) => {
  const filters = state.fuelCardTransactionItems.filters
  const filterValues: Record<
    string,
    string | string[] | number | undefined | null
  > = {}

  for (const filterName in filters) {
    if (
      filters[filterName as keyof FuelCardTransactionItemFilters]?.value != null
    ) {
      filterValues[filterName] =
        filters[filterName as keyof FuelCardTransactionItemFilters]?.value
    }
  }

  if (fetchIds != null) {
    filterValues.ids = fetchIds
  }

  const qs = queryString.stringify(
    { ...filterValues },
    { arrayFormat: 'bracket' },
  )

  return `/api/fuel_card_transaction_items${qs != '' ? `?${qs}` : ''}`
}

export const getMoneyCodesUrl = (state: State, _fetchIds?: UUID7[]) => {
  const filters = state.moneyCodes?.filters
  const filterValues: Record<string, string | string[] | number | null | undefined> = {}

  if (filters?.customer_id?.value && filters.customer_id.value.length > 0) {
    filterValues.customer_id = filters.customer_id.value
  }
  const textFilters = filters as
    | { code?: { value?: string | null }; issued_to?: { value?: string | null } }
    | undefined
  if (textFilters?.code?.value) {
    filterValues.code = textFilters.code.value
  }
  if (textFilters?.issued_to?.value) {
    filterValues.issued_to = textFilters.issued_to.value
  }
  // Created date range
  if (filters?.created_from?.value) {
    filterValues.created_from = filters.created_from.value
  }
  if (filters?.created_to?.value) {
    filterValues.created_to = filters.created_to.value
  }
  // Activated date range
  if (filters?.activated_from?.value) {
    filterValues.activated_from = filters.activated_from.value
  }
  if (filters?.activated_to?.value) {
    filterValues.activated_to = filters.activated_to.value
  }
  // First used date range
  if (filters?.first_used_from?.value) {
    filterValues.first_used_from = filters.first_used_from.value
  }
  if (filters?.first_used_to?.value) {
    filterValues.first_used_to = filters.first_used_to.value
  }
  if (filters?.used?.value && filters.used.value !== 'all') {
    filterValues.used = filters.used.value
  }
  if (filters?.billed?.value && filters.billed.value !== 'all') {
    filterValues.billed = filters.billed.value
  }
  if (filters?.page?.value != null) {
    filterValues.page = filters.page.value
  }
  if (filters?.limit?.value != null) {
    filterValues.limit = filters.limit.value
  }

  const qs = queryString.stringify(
    { ...filterValues },
    { arrayFormat: 'bracket' },
  )

  return `/api/money_code_transactions${qs != '' ? `?${qs}` : ''}`
}

const fetchUrlWithFilters = (
  state: State,
  key: 'fuelCards' | 'fuelCardTransactionItems' | 'users' | 'customers' | 'policies' | 'invoices' | 'receipts',
  fetchIds?: UUID7[],
  options?: { limit?: number },
) => {
  const target = state[key]
  const filters = target.filters
  const filterValues: Record<
    string,
    string | string[] | number | undefined | null
  > = {}

  for (const filterName in filters) {
    if (filters[filterName as keyof typeof filters]?.value != null) {
      filterValues[filterName] =
        filters[filterName as keyof typeof filters]?.value
    }
  }

  if (fetchIds != null) {
    filterValues.ids = fetchIds
  }

  const qs = queryString.stringify(
    { ...filterValues, ...(options ?? {}) },
    { arrayFormat: 'bracket' },
  )

  let path = ''
  switch (key) {
    case 'users':
      path = '/api/users'
      break
    case 'fuelCards':
      path = '/api/fuel_cards'
      break
    case 'fuelCardTransactionItems':
      path = '/api/fuel_card_transaction_items'
      break
    case 'customers':
      path = '/api/customers'
      break
    case 'policies':
      path = '/api/policies'
      break
    case 'invoices':
      path = '/api/invoices'
      break
    case 'receipts':
      path = '/api/receipts'
      break
  }
  return `${path}${qs != '' ? `?${qs}` : ''}`
}

export const fetchFuelCardTransactionItems =
  ({
    fetchIds,
  }: {
    fetchIds?: UUID7[]
    page?: number
  } = {}) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const url = getFetchFuelCardTransactionUrl(
        getState().organization,
        fetchIds,
      )
      const { data, metadata } = await (await fetch(url)).json()
      dispatch(
        slice.actions['fuelCardTransactionItems/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      console.error(err)
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchMoneyCodes =
  ({
    fetchIds,
  }: {
    fetchIds?: UUID7[]
    page?: number
  } = {}) =>
  async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['moneyCodes/request']())
      const url = getMoneyCodesUrl(getState().organization, fetchIds)
      const { data, metadata } = await (await fetch(url)).json()
      dispatch(
        slice.actions['moneyCodes/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      console.error(err)
      dispatch(
        slice.actions['moneyCodes/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const updateMoneyCode =
  (id: UUID7, updates: { customer_id?: string }) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['moneyCodes/request']())
      const { data } = await (
        await fetch(`/api/money_code_transactions/${id}`, {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(updates),
        })
      ).json()
      dispatch(
        slice.actions['moneyCodes/receive']({
          records: [data],
          merge: true,
          // sortBy: [['transaction_at', 'datetime', 'DESC']],
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['moneyCodes/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

/**
 * fetchFuelCardTransactionItemPreview
 *
 * Used to preview override changes to transaction items by calling
 * a server endpoint to return a rendered updated transacation item.
 * @param overrides - optional overrides to preview
 */
export const fetchFuelCardTransactionItemPreview = (id: string) => {
  return async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const response = await fetch(
        `/api/fuel_card_transaction_items/${id}/preview`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(
            getState().organization.fuelCardTransactionItems.previewOverrides,
          ),
        },
      )
      const { data } = await response.json()
      dispatch(
        slice.actions['fuelCardTransactionItems/updatePreview']({
          data,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }
}

/**
 * updateFuelCardTransactionItemOverrides
 *
 * Used to save override changes to transaction items
 * @param overrides - optional overrides to preview
 */
export const updateFuelCardTransactionItemOverrides = (id: string) => {
  return async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/request']())
      const response = await fetch(`/api/fuel_card_transaction_items/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          getState().organization.fuelCardTransactionItems.previewOverrides,
        ),
      })
      const { data } = await response.json()
      dispatch(
        slice.actions['fuelCardTransactionItems/receive']({
          records: [data],
          merge: true,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }
}

export const fetchInvoice =
  (invoiceId: UUID7) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['invoices/request']())
      const { data } = await (
        await appFetch(`/api/invoices/${invoiceId}`)
      ).json()
      dispatch(
        slice.actions['invoices/receive']({
          records: [data],
          merge: true,
          // sortBy: [['updated_at', 'datetime', 'DESC']],
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['invoices/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const fetchInvoices =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['invoices/request']())

      // Build query params from filters
      const filters = getState().organization.invoices.filters
      const params = new URLSearchParams()

      if (filters.status?.value) params.set('status', filters.status.value)
      if (filters.customer?.value) params.set('customer', filters.customer.value)
      if (filters.currency?.value) params.set('currency', filters.currency.value)
      if (filters.name?.value) params.set('name', filters.name.value)
      if (filters.truck?.value) params.set('truck', filters.truck.value)
      if (filters.updated_from?.value) params.set('updated_from', filters.updated_from.value)
      if (filters.updated_to?.value) params.set('updated_to', filters.updated_to.value)
      if (filters.page?.value != null) params.set('page', String(filters.page.value))
      if (filters.limit?.value != null) params.set('limit', String(filters.limit.value))

      const queryString = params.toString()
      const url = queryString ? `/api/invoices?${queryString}` : '/api/invoices'

      const { data, metadata } = await (await appFetch(url)).json()
      dispatch(
        slice.actions['invoices/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['invoices/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const createInvoice =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['invoices/request']())
      const { data } = await (
        await appFetch(`/api/invoices`, {
          method: 'POST',
          body: {
            invoice: {
              customer_id: getState().organization.invoices.create.customerId,
              location_id: getState().organization.invoices.create.locationId,
              fuel_card_id: getState().organization.invoices.create.fuelCardId,
              fuel_unit: getState().organization.invoices.create.fuelUnit,
              currency_code:
                getState().organization.invoices.create.currencyCode,
              name: getState().organization.invoices.create.name,
              due_date: getState().organization.invoices.create.dueDate,
              invoice_date: getState().organization.invoices.create.invoiceDate,
              start_at: getState().organization.invoices.create.startAt,
              end_at: getState().organization.invoices.create.endAt,
            },
          },
        })
      ).json()
      dispatch(
        slice.actions['invoices/receive']({
          records: [data],
          merge: true,
        }),
      )
      dispatch(slice.actions['invoices/updateCreate']({}))
      return data.id
    } catch (err) {
      dispatch(
        slice.actions['invoices/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const deleteInvoice =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['invoices/request']())
      const response = await fetch(`/api/invoices/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      if (response.status === 204) {
        // Hard delete — remove from store
        dispatch(slice.actions['invoices/delete']({ id }))
      } else if (response.ok) {
        // Void (soft delete) — update in store with returned invoice
        const { data } = await response.json()
        dispatch(slice.actions['invoices/receive']({ records: [data], merge: true }))
      }
    } catch (err) {
      dispatch(
        slice.actions['invoices/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const updateInvoice =
  (id: Invoice['id'], attrs: Partial<Invoice>) =>
  async (dispatch: AppDispatch) => {
    try {
      dispatch(slice.actions['invoices/request']())
      const body = JSON.stringify({ invoice: attrs })
      const response = await fetch(`/api/invoices/${id}`, {
        body: body,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const { data, errors } = await response.json()
      if (errors != null) {
        throw errors
      }
      dispatch(slice.actions['invoices/receive']({ records: [data] }))
    } catch (err) {
      dispatch(
        slice.actions['invoices/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
      throw err
    }
  }

export const fetchSources =
  () => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['sources/request']())
      const { data } = await (await appFetch(`/api/sources`)).json()
      dispatch(
        slice.actions['sources/receive']({
          records: data,
          merge: true,
          // sortBy: [['updated_at', 'datetime', 'DESC']],
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['sources/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const createFilterUrlString = <
  T extends Partial<PaginatedFilters> = Partial<PaginatedFilters>,
>(
  newFilters: T,
) => {
  const filterValues: Record<string, string | string[] | number | undefined> =
    {}

  for (const filterName in newFilters) {
    const filter = newFilters[filterName as keyof T]
    if (filter != null && typeof filter === 'object' && 'value' in filter) {
      const value = (filter as { value: unknown }).value
      if (value != null && value != '') {
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          (Array.isArray(value) && value.every((v) => typeof v === 'string'))
        ) {
          filterValues[filterName] = value
        }
      }
    }
  }
  let qs = queryString.stringify(filterValues, { arrayFormat: 'bracket' })
  if (qs != '') {
    qs = `?${qs}`
  }
  return qs
}

export const fetchCategories =
  () => async (dispatch: AppDispatch, _getState: AppGetState) => {
    dispatch(slice.actions['categories/request']())
    try {
      const data = await (
        await fetch('/api/fuel_card_transaction_items/categories')
      ).json()
      dispatch(slice.actions['categories/receive'](data))
    } catch (err) {
      dispatch(slice.actions['categories/receive']({ categories: [] }))
    }
  }

export const fetchJobs =
  () => async (dispatch: AppDispatch, _getState: AppGetState) => {
    dispatch(slice.actions['jobs/request']())
    try {
      const data = await (
        await appFetch('/api/organizations/refresh-all-data/status')
      ).json()
      dispatch(
        slice.actions['jobs/receive']({
          jobGroups: data.data,
        }),
      )
    } catch (err) {
      dispatch(slice.actions['jobs/receive']({ jobGroups: [] }))
    }
  }

export const fetchAllJobs =
  () => async (dispatch: AppDispatch, _getState: AppGetState) => {
    dispatch(slice.actions['jobs/request']())
    try {
      const data = await (
        await appFetch('/api/organizations/refresh-all-data/status')
      ).json()
      dispatch(
        slice.actions['jobs/receive']({
          jobGroups: data.data.job_groups,
        }),
      )
    } catch (err) {
      dispatch(slice.actions['jobs/receive']({ jobGroups: [] }))
    }
  }

export const fetchExternalDrivers =
  () => async (dispatch: AppDispatch, _getState: AppGetState) => {
    dispatch(slice.actions['externalDrivers/request']())
    try {
      const { data } = await (await appFetch('/api/external_drivers')).json()
      dispatch(slice.actions['externalDrivers/receive']({ records: data }))
    } catch (err) {
      dispatch(
        slice.actions['externalDrivers/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const setAllCards =
  (customerId: UUID7, status: string) =>
  async (dispatch: AppDispatch, _getState: AppGetState) => {
    const slowTimer = setTimeout(() => {
      dispatch(slice.actions['fuelCards/setAllCards/message']({
        text: 'This may take a moment — waiting for EFS to respond...',
        severity: 'info',
      }))
    }, 3000)
    try {
      dispatch(slice.actions['fuelCards/setAllCards/request']({ status }))
      const res = await appFetch(`/api/customers/${customerId}/all_cards`, {
        method: 'POST',
        body: { status },
      })
      const response = await res.json()
      clearTimeout(slowTimer)

      if (!res.ok) {
        const detail = response?.errors?.detail || 'Failed to update cards'
        dispatch(slice.actions['fuelCards/setAllCards/message']({ text: detail, severity: 'error' }))
        dispatch(slice.actions['fuelCards/setAllCards/receive']({ customerId }))
        return
      }

      const { data, skipped_count, error_count } = response

      const messages: string[] = []
      if (skipped_count > 0) {
        messages.push(
          `${skipped_count} card(s) skipped (on HOLD/FRAUD, managed by EFS/WEX)`,
        )
      }
      if (error_count > 0) {
        messages.push(`${error_count} card(s) failed to update`)
      }

      dispatch(
        slice.actions['fuelCards/setAllCards/receive']({
          customerId,
          cards: data,
        }),
      )
      dispatch(slice.actions['fuelCards/setAllCards/message']({
        text: messages.length > 0 ? messages.join('. ') : `Cards ${status === 'active' ? 'unblocked' : 'blocked'} successfully`,
        severity: messages.length > 0 ? 'info' : 'success',
      }))
    } catch (err) {
      clearTimeout(slowTimer)
      dispatch(slice.actions['fuelCards/setAllCards/message']({ text: 'Network error — failed to update cards', severity: 'error' }))
      dispatch(slice.actions['fuelCards/setAllCards/receive']({ customerId }))
    }
  }

export const addTransactionItem = () => {
  return async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['fuelCardTransactionItems/newItem/request']())
      const response = await appFetch(`/api/fuel_card_transactions`, {
        method: 'POST',
        body: {
          fuel_card_transaction:
            getState().organization.fuelCardTransactionItems.newItem.record,
        },
      })
      const payload = await response.json()
      if (!response.ok) {
        dispatch(
          slice.actions['fuelCardTransactionItems/newItem/receive']({
            errors: payload?.errors ?? { unknown: 'Failed to create transaction' },
          }),
        )
        return { ok: false }
      }
      dispatch(
        slice.actions['fuelCardTransactionItems/newItem/receive']({ data: payload.data }),
      )
      dispatch(fetchFuelCardTransactionItems({}))
      return { ok: true }
    } catch (err) {
      dispatch(
        slice.actions['fuelCardTransactionItems/newItem/receive']({
          errors: { unknown: err instanceof Error ? err.message : String(err) },
        }),
      )
      return { ok: false }
    }
  }
}

export const refreshAllTransactions = () => {
  return async (dispatch: AppDispatch, _getState: AppGetState) => {
    dispatch(slice.actions['jobs/refresh/request']())
    try {
      const { data: _data } = await (
        await appFetch(`/api/organizations/refresh-all-data/run`, {
          method: 'GET',
        })
      ).json()
      dispatch(slice.actions['jobs/refresh/receive']({}))
      dispatch(fetchJobs())
    } catch (err) {
      dispatch(
        slice.actions['jobs/refresh/receive']({
          errors: { unknown: 'Unknown error' },
        }),
      )
    }
  }
}

export const fetchReceipts =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    try {
      dispatch(slice.actions['receipts/request']())
      const url = fetchUrlWithFilters(getState().organization, 'receipts')
      const { data, metadata } = await (await appFetch(url)).json()
      dispatch(
        slice.actions['receipts/receive']({
          records: data,
          metadata,
          merge: false,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['receipts/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

export const fetchReceipt =
  (id: UUID7) => async (dispatch: AppDispatch, _getState: AppGetState) => {
    try {
      dispatch(slice.actions['receipts/request']())
      const { data } = await (await appFetch(`/api/receipts/${id}`)).json()
      dispatch(
        slice.actions['receipts/receive']({
          records: [data],
          merge: true,
        }),
      )
    } catch (err) {
      dispatch(
        slice.actions['receipts/reject']({
          error:
            err instanceof Error
              ? { unknown: String(err) }
              : (err as Record<string, string>),
        }),
      )
    }
  }

const augmentedSlice = {
  ...slice,
  actionCreators: {
    fetchFuelCardTransactionItems,
    fetchMoneyCodes,
    updateMoneyCode,
    fetchExternalDrivers,
    fetchInvoice,
    fetchInvoices,
    fetchReceipts,
    fetchReceipt,
    setAllCards,
    editFuelCardTransactionItem,
    reassignTransactionItemCustomer,
    fetchCustomer,
    fetchCustomers,
    fetchCategories,
    fetchFuelCards,
    fetchSources,
    addTransactionItem,
    refreshAllTransactions,
    addMultipleTransactionsToInvoice,
    addMultipleMoneyCodesToInvoice,
    removeMoneyCodeFromInvoice,
  },
}

export default augmentedSlice
